// The podium at the end of the race: where each cat sits, and what it gets up to while it is there.
//
// The first three cats stand on the 1st, 2nd and 3rd blocks; the others sit on the stage beside the
// blocks and on the grass in front, all facing the grandstand. Each cat then switches at random,
// on its own clock, between idle cat things: sitting, washing a paw or its face, looking around,
// flicking its tail, lying down, stretching and just standing. Both parts are pure, for tests.
import type { Personality } from '../../data/cats';
import type { CatId, RaceSnapshot } from '../../race/types';
import { PODIUM, podiumSpots } from '../environment/layout';

type ResultRow = NonNullable<RaceSnapshot['results']>[number];

export interface PodiumPlace {
  /** Where the cat's paws go, in world space. */
  x: number;
  y: number;
  z: number;
  /** Radians from +X towards +Z (the cat actor's convention); −π/2 faces the grandstand (−z). */
  heading: number;
  /** 1, 2 and 3 are on the blocks; later places are around them. */
  rank: number;
}

/** Faces the grandstand. */
export const PODIUM_HEADING = -Math.PI / 2;
/** Height of the stage the blocks stand on (see podium.ts). */
const STAGE_TOP = 0.16;

/** Places for the cats below third: beside the blocks on the stage, then on the grass in front. */
const AROUND: { dx: number; y: number; dz: number; turn: number }[] = [
  { dx: 2.75, y: STAGE_TOP, dz: -0.35, turn: -0.35 },
  { dx: -2.75, y: STAGE_TOP, dz: -0.35, turn: 0.35 },
  { dx: 0.85, y: 0, dz: -1.75, turn: 0.15 },
];

/**
 * Where every cat goes for the results, by official place. Disqualified cats never stand on a
 * block; they go after the others. Cats missing from the results are not placed.
 */
export function podiumPlaces(results: readonly ResultRow[]): Map<CatId, PodiumPlace> {
  const order = [...results].sort((a, b) => Number(a.dsq) - Number(b.dsq) || a.place - b.place);
  const spots = podiumSpots();
  const out = new Map<CatId, PodiumPlace>();
  order.forEach((r, i) => {
    const s = spots[i];
    if (s) { out.set(r.id, { x: s.x, y: s.y, z: s.z, heading: PODIUM_HEADING, rank: i + 1 }); return; }
    const a = AROUND[(i - spots.length) % AROUND.length]!;
    const lap = Math.floor((i - spots.length) / AROUND.length);
    out.set(r.id, { x: PODIUM.x + a.dx + lap * 0.6, y: a.y, z: PODIUM.z + a.dz, heading: PODIUM_HEADING + a.turn, rank: i + 1 });
  });
  return out;
}

/** The things a cat does on the podium. */
export type PodiumBehaviour = 'sit' | 'washPaw' | 'washFace' | 'lookAround' | 'tailFlick' | 'lieDown' | 'stretch' | 'stand';

export const PODIUM_BEHAVIOURS: readonly PodiumBehaviour[] = ['sit', 'washPaw', 'washFace', 'lookAround', 'tailFlick', 'lieDown', 'stretch', 'stand'];

/** Seconds each behaviour lasts: [shortest, longest]. A stretch is one long stretch. */
export const HOLD_S: Record<PodiumBehaviour, [number, number]> = {
  sit: [4, 9], washPaw: [4, 8], washFace: [3.5, 6], lookAround: [4, 8], tailFlick: [3, 6],
  lieDown: [7, 14], stretch: [3.2, 3.2], stand: [3, 6],
};

/** Relative chance of each behaviour for a personality, from its after-race sit/groom/idle mix. */
export function behaviourWeights(p: Personality): Record<PodiumBehaviour, number> {
  const { sit, groom, idle } = p.settle;
  const lazy = 1 - p.energy; // calm cats lie down more, busy ones look around and stand
  return {
    sit: sit,
    washPaw: groom,
    washFace: groom * 0.8,
    lookAround: idle * (0.6 + p.energy * 0.6),
    tailFlick: (sit + idle) * 0.35,
    lieDown: (sit + 0.5) * (0.4 + lazy),
    stretch: 0.45,
    stand: idle * 0.5 * (0.5 + p.energy),
  };
}

/** What may follow a behaviour: a lying cat sits up or stretches before it does anything busy. */
function allowedAfter(prev: PodiumBehaviour | null, b: PodiumBehaviour): boolean {
  if (b === prev) return false;
  if (prev === 'lieDown') return b === 'sit' || b === 'stretch' || b === 'tailFlick';
  if (prev === 'stretch') return b === 'stand' || b === 'sit' || b === 'lookAround';
  return true;
}

export interface PodiumStep { behaviour: PodiumBehaviour; hold: number }

/**
 * Picks one cat's next behaviour and how long to keep at it. Deterministic for a seed, so a cat
 * does the same things on every run, but each cat (own seed) runs on its own clock.
 */
export class PodiumIdle {
  private s: number;
  private prev: PodiumBehaviour | null = null;
  readonly weights: Record<PodiumBehaviour, number>;

  constructor(p: Personality, seed: number) {
    this.weights = behaviourWeights(p);
    this.s = (Math.abs(Math.floor(seed)) % 2147483646) + 1;
  }

  private rnd(): number { this.s = (this.s * 48271) % 2147483647; return this.s / 2147483647; }

  /** The first behaviour once on the podium: sat down, with a short first hold so cats drift apart. */
  first(): PodiumStep {
    this.prev = 'sit';
    return { behaviour: 'sit', hold: 1.5 + this.rnd() * 4 };
  }

  next(): PodiumStep {
    const options = PODIUM_BEHAVIOURS.filter((b) => allowedAfter(this.prev, b) && this.weights[b] > 0);
    const total = options.reduce((t, b) => t + this.weights[b], 0);
    let r = this.rnd() * total;
    let pick = options[options.length - 1]!;
    for (const b of options) { r -= this.weights[b]; if (r <= 0) { pick = b; break; } }
    const [lo, hi] = HOLD_S[pick];
    this.prev = pick;
    return { behaviour: pick, hold: lo + this.rnd() * (hi - lo) };
  }
}

/** Procedural layers the secondary motion adds on top of a clip for the podium behaviours. */
export type PoseLayer = 'lie' | 'stretch' | 'face' | 'flick' | 'lookAround';
export const POSE_LAYERS: readonly PoseLayer[] = ['lie', 'stretch', 'face', 'flick', 'lookAround'];

/** The clip a behaviour plays and the layer, if any, added on top. */
export const BEHAVIOUR_POSE: Record<PodiumBehaviour, { clip: 'sit' | 'groom' | 'idle'; layer: PoseLayer | null }> = {
  sit: { clip: 'sit', layer: null },
  washPaw: { clip: 'groom', layer: null },
  washFace: { clip: 'groom', layer: 'face' },
  lookAround: { clip: 'sit', layer: 'lookAround' },
  tailFlick: { clip: 'sit', layer: 'flick' },
  lieDown: { clip: 'sit', layer: 'lie' },
  stretch: { clip: 'idle', layer: 'stretch' },
  stand: { clip: 'idle', layer: null },
};
